import type { SalesTaxLineV2 } from "@credit-acceptance/step6-bff-client";
import { useTranslation } from "react-i18next";

import { formatCurrency, formatSignedCurrency } from "../../../../lib/money";
import type { AuditTableColumn } from "../../../audit/AuditTable";
import SummaryTableModal from "../../components/SummaryTableModal";
import type { SalesTaxMessageKey } from "./salesTaxMessage";

interface SalesTaxModalProperties {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  lines: SalesTaxLineV2[];
  total?: number;
  /** Marks the total as manually entered. */
  isManual?: boolean;
  /** The deal's total selling price, shown in place of the vehicle selling price row's amount. */
  totalSellingPrice?: number;
  /** State-rule info message shown below the table. */
  messageKey?: SalesTaxMessageKey;
}

const VEHICLE_SELLING_PRICE_ROW = "VEHICLE SELLING PRICE";

const BLANK = "";

// Null amounts and methods render blank; zero stays `0.00`.
const formatBlankable = (amount?: number | null) =>
  amount === undefined || amount === null ? BLANK : formatCurrency(amount);

// Read-only per-item sales tax breakdown, opened from the "Sales Tax" link
function SalesTaxModal({
  open,
  onOpenChange,
  lines,
  total,
  isManual = false,
  totalSellingPrice,
  messageKey,
}: SalesTaxModalProperties) {
  const { t } = useTranslation();

  // Legacy shows the vehicle selling price row as the deal's TOTAL SELLING PRICE (exact, case-sensitive match).
  const rows = lines.map((line) =>
    line.name === VEHICLE_SELLING_PRICE_ROW
      ? {
          ...line,
          name: t("financial.salesTaxModal.totalSellingPriceItem"),
          taxableAmount: totalSellingPrice ?? line.taxableAmount,
        }
      : line
  );

  const columns: Array<AuditTableColumn<SalesTaxLineV2>> = [
    { key: "name", header: t("financial.modalColumns.taxableItem"), render: (line) => line.name ?? BLANK },
    {
      key: "taxableAmount",
      header: t("financial.modalColumns.amount"),
      className: "text-right",
      headerClassName: "text-right",
      render: (line) => formatBlankable(line.taxableAmount),
    },
    {
      key: "taxMethod",
      header: t("financial.modalColumns.taxMethod"),
      className: "text-center",
      headerClassName: "text-center",
      render: (line) => line.taxMethod ?? BLANK,
    },
    {
      key: "taxAmount",
      header: t("financial.modalColumns.taxAmount"),
      className: "text-right",
      headerClassName: "text-right",
      render: (line) => formatBlankable(line.taxAmount),
    },
  ];

  const manualMarker = isManual ? ` ${t("financial.salesTaxModal.manuallyEntered")}` : "";
  const totalLine = `${formatSignedCurrency(total ?? 0)}${manualMarker}`;

  const bottomContent =
    rows.length === 0 || messageKey ? (
      <>
        {rows.length === 0 && <p>{t("financial.salesTaxModal.empty")}</p>}
        {messageKey && <p className="font-(--font-weight-bold)">{t(messageKey)}</p>}
      </>
    ) : undefined;

  return (
    <SummaryTableModal
      open={open}
      onOpenChange={onOpenChange}
      title={t("financial.salesTaxModal.title")}
      description={t("financial.salesTaxModal.description")}
      caption={t("financial.salesTaxModal.caption")}
      summaryLines={[{ label: t("financial.salesTaxModal.total"), value: totalLine }]}
      columns={columns}
      rows={rows}
      bottomContent={bottomContent}
      contentClassName="sm:max-w-2xl"
      tableDensity="compact"
      tableBodyClassName="text-(length:--typography-base-sizes-extra-small-font-size)/(--typography-base-sizes-extra-small-line-height)"
    />
  );
}

export default SalesTaxModal;
